import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import Navbar from './utils/Navbr';
import './style/ManageOffers.css';

// --- MOCK LOCATION DATA (Move to src/data/locations.json) ---
// Instructions: Create a file 'locations.json', import it like: import LOCATIONS_DATA from '../data/locations.json';
const LOCATIONS_DATA = {
    countries: [
        { name: "India", code: "IN", states: ["Delhi", "Maharashtra", "Karnataka"] },
        { name: "USA", code: "US", states: ["California", "New York", "Texas"] },
        { name: "UK", code: "UK", states: ["London", "Manchester"] }
    ],
    cities: {
        "Delhi": ["New Delhi", "Dwarka"],
        "Maharashtra": ["Mumbai", "Pune"],
        "California": ["Los Angeles", "San Francisco"],
        "New York": ["NYC", "Buffalo"]
    }
};

// --- HELPER COMPONENTS ---

// 1. Custom Toggle Switch
const CustomToggle_op = ({ label, checked, onChange }) => (
    <div className="toggle-wrapper_op" onClick={() => onChange(!checked)}>
        <div className={`toggle-switch_op ${checked ? 'active' : ''}`}>
            <div className="toggle-knob_op"></div>
        </div>
        <span className="toggle-label_op">{label}</span>
    </div>
);

// 2. Custom Select Dropdown
const CustomSelect_op = ({ options, value, onChange, placeholder = "Select..." }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="custom-select-container_op" ref={ref}>
            <div className={`select-trigger_op ${isOpen ? 'open' : ''}`} onClick={() => setIsOpen(!isOpen)}>
                <span>{value || placeholder}</span>
                <i className="arrow-down_op">▼</i>
            </div>
            {isOpen && (
                <div className="select-options_op">
                    {options.map((opt, idx) => (
                        <div key={idx} className="select-option_op" onClick={() => { onChange(opt); setIsOpen(false); }}>
                            {opt}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// 3. Tag Input (Generic)
const TagInput_op = ({ tags, setTags, suggestions = [], placeholder }) => {
    const [input, setInput] = useState('');
    const [filteredSuggestions, setFiltered] = useState([]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addTag(input);
        }
    };

    const addTag = (val) => {
        const trimmed = val.trim();
        if (trimmed && !tags.includes(trimmed)) {
            setTags([...tags, trimmed]);
            setInput('');
            setFiltered([]);
        }
    };

    const removeTag = (tagToRemove) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    const handleChange = (e) => {
        const val = e.target.value;
        setInput(val);
        if (suggestions.length > 0 && val) {
            setFiltered(suggestions.filter(s => s.toLowerCase().includes(val.toLowerCase())));
        } else {
            setFiltered([]);
        }
    };

    return (
        <div className="tag-input-wrapper_op">
            <div className="tags-container_op">
                {tags.map((tag, idx) => (
                    <span key={idx} className="tag_op">{tag} <b onClick={() => removeTag(tag)}>×</b></span>
                ))}
                <input
                    type="text"
                    value={input}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className="tag-input-field_op"
                />
            </div>
            {filteredSuggestions.length > 0 && (
                <div className="suggestions-box_op">
                    {filteredSuggestions.map((s, i) => (
                        <div key={i} className="suggestion-item_op" onClick={() => addTag(s)}>{s}</div>
                    ))}
                </div>
            )}
        </div>
    );
};

// --- MAIN PAGE COMPONENT ---
const ManageOffers = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [offers, setOffers] = useState([]);

    // Auth Token
    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };
    const adminToken = getCookie('adminToken');

    // Modals
    const [showModal, setShowModal] = useState(false); // Add/Edit
    const [showViewModal, setShowViewModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [viewData, setViewData] = useState(null);

    // Form State
    const initialFormState = {
        offerName: '', offerId: '', logo: '', trackingLink: '',
        description: '', category: '', type: '',
        oneTimeOneDevice: 'No',
        needApproval: 'No', 
        blockedStates: [],
        geoTargeting: false,
        geoCountries: [], geoStates: [], geoCities: [],
        ipWhitelist: false, ipList: [],
        createCampaign: false,
        status: 'Active', 
        events: [], // Array of event objects
        cutback: '' // --- NEW: Added single cutback field at offer level ---
    };
    const [formData, setFormData] = useState(initialFormState);

    // IP Input Timer Ref
    const ipTimerRef = useRef(null);
    const [ipInputVal, setIpInputVal] = useState('');

    // --- AUTH & INIT ---
    useEffect(() => {
        const init = async () => {
            try {
                // ✅ 1. Admin Auth Check
                const authRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'checkAuthAdmin',
                    adminToken
                });

                if (!authRes.data.status) {
                    toast.error(authRes.data.message || 'Admin session expired');
                    navigate('/cbMediaAdmin7/login'); // Redirect to your specific admin login route
                    return;
                }

                // ✅ 2. If Auth OK → Fetch Offers
                fetchOffers();

            } catch (err) {
                // toast.error('Admin auth check failed');
                // navigate('/cbMediaAdmin7/login');
            } finally {
                setLoading(false);
            }
        };

        if (adminToken) init();
        else {
            // For Demo purposes if token missing, just load empty or mock
            setLoading(false);
            // navigate('/cbMediaAdmin7/login'); 
        }
    }, [navigate, adminToken]);

    const fetchOffers = async () => {
        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'fetchAllOffers',
                adminToken
            });

            if (res.data.status) {
                const mappedOffers = (res.data.offers || []).map(o => ({
                    // ✅ MUST match UI keys
                    offerId: o.offerId,
                    offerName: o.offer_name,
                    logo: o.logo,
                    trackingLink: o.trackLink,
                    description: o.desc,
                    category: o.category,
                    type: o.type,
                    oneTimeOneDevice: (o.oneDevOneTime === 'true' ? 'Yes' : 'No'),
                    needApproval: (o.needApproval === 'true' ? 'Yes' : 'No'), 
                    blockedStates: (() => {
                        try { return JSON.parse(o.blockedstates || '[]'); }
                        catch { return []; }
                    })(),
                    geoTargeting: true,
                    geoCountries: (() => { try { return JSON.parse(o.country || '[]'); } catch { return []; } })(),
                    geoStates: (() => { try { return JSON.parse(o.state || '[]'); } catch { return []; } })(),
                    geoCities: (() => { try { return JSON.parse(o.city || '[]'); } catch { return []; } })(),
                    ipWhitelist: false,
                    ipList: [],
                    createCampaign: false,
                    status: (o.status === 'active' ? 'Active' : o.status),
                    cutback: o.cutback || '', // ✅ Map single cutback key safely from API

                    // ✅ events from eventConfig
                    events: (() => {
                        try {
                            const ev = JSON.parse(o.eventConfig || '[]');
                            return Array.isArray(ev) ? ev.map(e => ({
                                name: e.eventName,
                                id: e.eventId,
                                payout: e.payout,
                                ownPayout: e.own_payout,
                                totalCap: e.tCaps,
                                dailyCap: e.dCaps
                            })) : [];
                        } catch {
                            return [];
                        }
                    })()
                }));

                setOffers(mappedOffers);
            }
        } catch (e) {
            console.error(e);
        }
    };


    // --- FORM HANDLERS ---

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleEventChange = (index, field, value) => {
        const newEvents = [...formData.events];
        newEvents[index][field] = value;
        setFormData(prev => ({ ...prev, events: newEvents }));
    };

    const addEvent = () => {
        setFormData(prev => ({
            ...prev,
            events: [...prev.events, { name: '', id: '', payout: '', ownPayout: '', totalCap: '', dailyCap: '' }]
        }));
    };

    const removeEvent = (index) => {
        const newEvents = formData.events.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, events: newEvents }));
    };

    // Geo Logic
    const getAvailableStates = () => {
        if (formData.geoCountries.length === 0) return [];
        let states = [];
        formData.geoCountries.forEach(cName => {
            const country = LOCATIONS_DATA.countries.find(c => c.name === cName);
            if (country) states = [...states, ...country.states];
        });
        return states;
    };

    const getAvailableCities = () => {
        if (formData.geoStates.length === 0) return [];
        let cities = [];
        formData.geoStates.forEach(sName => {
            if (LOCATIONS_DATA.cities[sName]) cities = [...cities, ...LOCATIONS_DATA.cities[sName]];
        });
        return cities;
    };

    // IP Input Logic (Pause/Enter/Space)
    const handleIpInput = (e) => {
        const val = e.target.value;
        setIpInputVal(val);

        if (ipTimerRef.current) clearTimeout(ipTimerRef.current);

        // Check for Space or Enter
        if (val.endsWith(' ') || e.key === 'Enter') {
            const ip = val.trim();
            if (ip) {
                setFormData(prev => ({ ...prev, ipList: [...prev.ipList, ip] }));
                setIpInputVal('');
            }
        } else {
            // 1.5s Pause Logic
            ipTimerRef.current = setTimeout(() => {
                if (val.trim()) {
                    setFormData(prev => ({ ...prev, ipList: [...prev.ipList, val.trim()] }));
                    setIpInputVal('');
                }
            }, 1500);
        }
    };

    // --- SUBMIT ---
    const handleSubmit = async () => {
        if (!formData.offerName || !formData.offerId) return toast.warning("Name and ID are required");

        const action = isEditMode ? 'adminUpdateOffer' : 'adminAddOffer';

        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action,
                adminToken,
                data: formData
            });

            if (res.data.status) {
                toast.success(res.data.message);
                setShowModal(false);
                fetchOffers();
            } else {
                toast.error(res.data.message);
            }
        } catch (e) {
            toast.error("Server Error");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'adminDeleteOffer', adminToken, id
            });
            if (res.data.status) {
                toast.success("Deleted");
                fetchOffers();
            }
        } catch (e) { toast.error("Error deleting"); }
    };

    // --- RENDER HELPERS ---
    const openAddModal = () => {
        setFormData(initialFormState);
        setIsEditMode(false);
        setShowModal(true);
    };

    const openEditModal = (offer) => {
        setIsEditMode(true);
        setFormData({
            ...formData,
            ...offer 
        });
        setShowModal(true);
    };


    const openViewModal = (offer) => {
        setViewData(offer); 
        setShowViewModal(true);
    };

    if (loading) return <Loader />;

    return (
        <div className="manage-offers-wrapper_op">
            <Navbar name="Admin" role="Administrator" isAdmin={true} />
            <div className="bg-shape_op shape-1_op"></div>
            <div className="bg-shape_op shape-2_op"></div>

            <div className="content-container_op">

                {/* Header */}
                <div className="page-header_op">
                    <h2 className="page-title_op">Manage <span className="highlight_op">Offers</span></h2>
                    <button className="add-btn_op wave-effect_op" onClick={openAddModal}>
                        <span>+ ADD OFFER</span>
                        <div className="wave_op"></div>
                    </button>
                </div>

                {/* Table */}
                <div className="table-card_op">
                    <table className="offers-table_op">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Logo</th>
                                <th>Name</th>
                                <th>Payout</th>
                                <th>Type</th>
                                <th>Category</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {offers.length > 0 ? offers.map((offer) => (
                                <tr key={offer.offerId}>
                                    <td>{offer.offerId}</td>
                                    <td><img src={offer.logo} alt="" className="table-logo_op" /></td>
                                    <td>{offer.offerName}</td>
                                    <td className="payout-text_op">₹{offer.events?.[0]?.payout || '0'}</td>
                                    <td><span className="badge_op type_op">{offer.type}</span></td>
                                    <td>{offer.category}</td>
                                    <td>
                                        <div className="action-btns_op">
                                            <button className="icon-btn_op view" onClick={() => openViewModal(offer)}>👁️</button>
                                            <button className="icon-btn_op edit" onClick={() => openEditModal(offer)}>✏️</button>
                                            <button className="icon-btn_op delete" onClick={() => handleDelete(offer.offerId)}>🗑️</button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="7" className="no-data_op">No Offers Found</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- ADD/EDIT MODAL --- */}
            {showModal && (
                <div className="modal-overlay_op">
                    <div className="modal-content_op fade-in_op">
                        <div className="modal-header_op">
                            <h3>{isEditMode ? 'Edit Offer' : 'Add New Offer'}</h3>
                            <button className="close-btn_op" onClick={() => setShowModal(false)}>&times;</button>
                        </div>

                        <div className="modal-body_op scrollable_op">

                            {/* Basic Info */}
                            <div className="form-group_op">
                                <label>Offer Name</label>
                                <input type="text" name="offerName" value={formData.offerName} onChange={handleInputChange} placeholder="e.g. Flipkart Install" />
                            </div>

                            <div className="form-row_op">
                                <div className="form-group_op half">
                                    <label>Offer ID</label>
                                    <input type="text" name="offerId" value={formData.offerId} onChange={handleInputChange} disabled={isEditMode} />
                                </div>
                                <div className="form-group_op half">
                                    <label>Logo Link</label>
                                    <input type="text" name="logo" value={formData.logo} onChange={handleInputChange} />
                                </div>
                            </div>

                            <div className="form-group_op">
                                <label>Tracking Link</label>
                                <input type="text" name="trackingLink" value={formData.trackingLink} onChange={handleInputChange} />
                                <div className="macros-hint_op">
                                    Macros: <span>{`{tid}`}</span> <span>{`{aff_id}`}</span> <span>{`{click_id}`}</span> <span>{`{sub_id}`}</span>
                                </div>
                            </div>

                            {/* Events Section */}
                            <div className="events-section_op">
                                <div className="events-header_op">
                                    <h4>Conversion Events</h4>
                                    <button className="small-add-btn_op" onClick={addEvent}>+ Add Event</button>
                                </div>
                                {formData.events.map((evt, idx) => (
                                    <div key={idx} className="event-card_op">
                                        <button className="remove-evt-btn_op" onClick={() => removeEvent(idx)}>&times;</button>
                                        <div className="form-row_op">
                                            <input placeholder="Event Name" value={evt.name} onChange={(e) => handleEventChange(idx, 'name', e.target.value)} />
                                            <input placeholder="Event ID" value={evt.id} onChange={(e) => handleEventChange(idx, 'id', e.target.value)} />
                                        </div>
                                        <div className="form-row_op four-col">
                                            <input placeholder="Payout" value={evt.payout} onChange={(e) => handleEventChange(idx, 'payout', e.target.value)} />
                                            <input placeholder="Own Payout" value={evt.ownPayout} onChange={(e) => handleEventChange(idx, 'ownPayout', e.target.value)} />
                                            <input placeholder="Total Cap" value={evt.totalCap} onChange={(e) => handleEventChange(idx, 'totalCap', e.target.value)} />
                                            <input placeholder="Daily Cap" value={evt.dailyCap} onChange={(e) => handleEventChange(idx, 'dailyCap', e.target.value)} />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* --- NEW: Single Cutback field input right after event config section --- */}
                            <div className="form-group_op" style={{ marginTop: '1rem' }}>
                                <label>Cutback</label>
                                <input type="text" name="cutback" value={formData.cutback} onChange={handleInputChange} placeholder="e.g. 10%" />
                            </div>

                            {/* Description Selects */}
                            <div className="form-group_op" style={{ marginTop: '1rem' }}>
                                <label>Description</label>
                                <textarea name="description" value={formData.description} onChange={handleInputChange} rows="3"></textarea>
                            </div>

                            <div className="form-row_op four-col">
                                <div className="form-group_op">
                                    <label>Category</label>
                                    <CustomSelect_op
                                        options={['Finance', 'Education', 'E-commerce', 'Gaming', 'Health', 'Travel', 'Utilities', 'Food & Delivery', 'Dating', 'Crypto']}
                                        value={formData.category}
                                        onChange={(val) => setFormData({ ...formData, category: val })}
                                    />
                                </div>
                                <div className="form-group_op">
                                    <label>Type</label>
                                    <CustomSelect_op
                                        options={['CPI', 'CPR', 'CPA', 'CPL', 'CPS', 'CPC', 'CPM']}
                                        value={formData.type}
                                        onChange={(val) => setFormData({ ...formData, type: val })}
                                    />
                                </div>

                                <div className="form-group_op">
                                    <label>One Time One Device</label>
                                    <CustomSelect_op
                                        options={['Yes', 'No']}
                                        value={formData.oneTimeOneDevice}
                                        onChange={(val) => setFormData({ ...formData, oneTimeOneDevice: val })}
                                    />
                                </div>
                                
                                <div className="form-group_op">
                                    <label>Need Approval</label>
                                    <CustomSelect_op
                                        options={['Yes', 'No']}
                                        value={formData.needApproval}
                                        onChange={(val) => setFormData({ ...formData, needApproval: val })}
                                    />
                                </div>
                            </div>

                            {/* Blocked States */}
                            <div className="form-group_op">
                                <label>Blocked States</label>
                                <TagInput_op
                                    tags={formData.blockedStates}
                                    setTags={(tags) => setFormData({ ...formData, blockedStates: tags })}
                                    suggestions={LOCATIONS_DATA.countries[0].states} 
                                    placeholder="Type state & enter..."
                                />
                            </div>

                            {/* Geo Targeting */}
                            <div className="feature-section_op">
                                <CustomToggle_op
                                    label="Geo Targeting"
                                    checked={formData.geoTargeting}
                                    onChange={(val) => setFormData({ ...formData, geoTargeting: val })}
                                />
                                {formData.geoTargeting && (
                                    <div className="geo-inputs_op fade-in_op">
                                        <div className="form-group_op">
                                            <label>Select Countries</label>
                                            <TagInput_op
                                                tags={formData.geoCountries}
                                                setTags={(tags) => setFormData({ ...formData, geoCountries: tags, geoStates: [], geoCities: [] })}
                                                suggestions={LOCATIONS_DATA.countries.map(c => c.name)}
                                                placeholder="Add Countries..."
                                            />
                                        </div>
                                        <div className="form-group_op">
                                            <label>Select States</label>
                                            <TagInput_op
                                                tags={formData.geoStates}
                                                setTags={(tags) => setFormData({ ...formData, geoStates: tags, geoCities: [] })}
                                                suggestions={getAvailableStates()}
                                                placeholder="Add States..."
                                            />
                                        </div>
                                        <div className="form-group_op">
                                            <label>Select Cities</label>
                                            <TagInput_op
                                                tags={formData.geoCities}
                                                setTags={(tags) => setFormData({ ...formData, geoCities: tags })}
                                                suggestions={getAvailableCities()}
                                                placeholder="Add Cities..."
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* IP Whitelist */}
                            <div className="feature-section_op">
                                <CustomToggle_op
                                    label="IP Whitelist"
                                    checked={formData.ipWhitelist}
                                    onChange={(val) => setFormData({ ...formData, ipWhitelist: val })}
                                />
                                {formData.ipWhitelist && (
                                    <div className="geo-inputs_op fade-in_op">
                                        <label>Allowed IPs (Type & pause or press enter)</label>
                                        <div className="tag-input-wrapper_op">
                                            <div className="tags-container_op">
                                                {formData.ipList.map((ip, i) => (
                                                    <span key={i} className="tag_op">{ip} <b onClick={() => setFormData(p => ({ ...p, ipList: p.ipList.filter(x => x !== ip) }))}>×</b></span>
                                                ))}
                                                <input
                                                    type="text"
                                                    className="tag-input-field_op"
                                                    value={ipInputVal}
                                                    onChange={handleIpInput}
                                                    onKeyDown={handleIpInput}
                                                    placeholder="192.168.1.1"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Create Campaign */}
                            <div className="feature-section_op">
                                <CustomToggle_op
                                    label="Create Campaign Automatically"
                                    checked={formData.createCampaign}
                                    onChange={(val) => setFormData({ ...formData, createCampaign: val })}
                                />
                            </div>

                            {/* Edit Only: Status */}
                            {isEditMode && (
                                <div className="form-group_op" style={{ marginTop: '1rem' }}>
                                    <label>Status</label>
                                    <CustomSelect_op
                                        options={['Active', 'Paused', 'Disabled']}
                                        value={formData.status}
                                        onChange={(val) => setFormData({ ...formData, status: val })}
                                    />
                                </div>
                            )}

                        </div>

                        <div className="modal-footer_op">
                            <button className="submit-btn_op" onClick={handleSubmit}>
                                {isEditMode ? 'UPDATE OFFER' : 'ADD OFFER'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- VIEW MODAL --- */}
            {showViewModal && viewData && (
                <div className="modal-overlay_op" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content_op view-mode_op fade-in_op" onClick={e => e.stopPropagation()}>
                        <div className="modal-header_op">
                            <h3>Offer Details: <span className="highlight_op">{viewData.offerName}</span></h3>
                            <button className="close-btn_op" onClick={() => setShowViewModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body_op scrollable_op">
                            <div className="view-grid_op">
                                <div className="view-item_op"><label>ID:</label> <span>{viewData.offerId}</span></div>
                                <div className="view-item_op"><label>Type:</label> <span className="badge_op type_op">{viewData.type}</span></div>
                                <div className="view-item_op"><label>Category:</label> <span>{viewData.category}</span></div>
                                <div className="view-item_op"><label>Approval Required:</label> <span>{viewData.needApproval}</span></div>
                                <div className="view-item_op"><label>Cutback:</label> <span>{viewData.cutback || 'N/A'}</span></div>
                                <div className="view-item_op"><label>Events:</label>
                                    <div className="tags-view_op">
                                        {viewData.events?.map(e => <span className="tag_op" key={e.id}>{e.name} (₹{e.payout})</span>)}
                                    </div>
                                </div>
                                <div className="view-item_op full-w"><label>Tracking Link:</label> <code className="code-block_op">{viewData.trackingLink}</code></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ManageOffers;